// A borda entre o documento autoral e o blob que a API grava. TODA esquisitice do
// formato morre aqui (D6) — o `SudokuDoc` fica limpo.
//
// As três que importam:
//
// 1. **`x` é a LINHA e `y` é a COLUNA** (§4.2), ambos 1-based e em STRING. O player
//    (`games/Sudoku/Game/Board`) filtra com `Number(x) === row + 1`. Inverter não
//    quebra nada visível aqui no editor — quebra o jogo do usuário, que passa a
//    renderizar só a diagonal. Por isso a LEITURA também usa o par (x, y) para achar
//    o índice, em vez de confiar na ordem do array: assim uma inversão aparece no
//    round-trip do teste em vez de se cancelar.
// 2. **O blob trafega como STRING JSON**, não como objeto — ao contrário do
//    caça-palavras. O `CreateSudokuDto` do backend é `@IsString()` e o player faz
//    `JSON.parse(response.data.game)`.
// 3. **`hint` é a string `'true'`/`''`**, não boolean. E `solution` vai em TODAS as
//    casas, inclusive nas vazias: é contra ela que o player confere o que o jogador
//    digita.

import { alphabetFor, parseAlphabet, shapeFromGame } from '../../../../../games/Sudoku/shape';
import type { iGameSudoku } from '../../../../../interfaces/sudoku';
import {
  cellIndex,
  colOf,
  emptyDoc,
  rowOf,
  type SudokuDificuldade,
  type SudokuDoc,
} from './doc';
import { GIVENS_POR_DIFICULDADE } from './solver';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/**
 * A `dificuldade` NÃO trafega no blob (comentário do topo, item 2) — não dá para
 * inventar o campo, o validador do servidor reprova casa fora do formato canônico.
 * A única pista disponível depois do round-trip é quantas casas vieram dadas.
 *
 * Comparamos a contagem contra `GIVENS_POR_DIFICULDADE` (os mesmos alvos que
 * `generateGrid`, solver.ts, usa para gerar) e devolvemos a mais próxima. É uma
 * ESTIMATIVA, não um fato: o admin pode ter ligado/desligado casas à mão depois de
 * gerar, o que desloca a contagem sem mudar a dificuldade "de verdade" — mas é
 * estritamente melhor do que o antigo default fixo em `'medio'`, que mostrava
 * "Médio" mesmo para um tabuleiro salvo em "Difícil" e deixava o próximo "Gerar"
 * sobrescrever o trabalho do autor com a dificuldade errada em silêncio.
 */
function inferDificuldade(givens: boolean[]): SudokuDificuldade {
  const dadas = givens.filter(Boolean).length;
  let melhor: SudokuDificuldade = 'medio';
  let menorDiferenca = Infinity;
  (Object.keys(GIVENS_POR_DIFICULDADE) as SudokuDificuldade[]).forEach((candidata) => {
    const diferenca = Math.abs(GIVENS_POR_DIFICULDADE[candidata] - dadas);
    if (diferenca < menorDiferenca) {
      menorDiferenca = diferenca;
      melhor = candidata;
    }
  });
  return melhor;
}

/** O documento vira a STRING JSON que `create_sudoku`/`PUT .../sudoku` gravam. */
export function docToBlob(doc: SudokuDoc): string {
  const cell: iGameSudoku['crossword-compiler']['rectangular-puzzle']['sudoku']['grid']['cell'] =
    [];
  const { size, boxW, boxH } = doc.shape;
  for (let index = 0; index < size * size; index++) {
    cell.push({
      attributes: {
        x: String(rowOf(index, size) + 1), // LINHA — sim, é `x`. Ver §4.2.
        y: String(colOf(index, size) + 1), // COLUNA
        solution: String(doc.solution[index] ?? 0),
        hint: doc.givens[index] ? 'true' : '',
      },
    });
  }

  const game: iGameSudoku = {
    'crossword-compiler': {
      'rectangular-puzzle': {
        attributes: { alphabet: alphabetFor(doc.shape) },
        sudoku: {
          attributes: { 'box-width': String(boxW), 'box-height': String(boxH) },
          grid: { attributes: { width: String(size), height: String(size) }, cell },
        },
      },
    },
  };

  return JSON.stringify(game);
}

/**
 * O caminho de volta. `raw` é o que `show_sudoku` devolve em `game`: uma STRING
 * JSON. Aceitamos também o objeto já desserializado — a coluna é `Json` no Prisma e
 * já houve registro gravado como objeto; um `typeof` aqui custa nada e evita que o
 * editor abra vazio nesses casos.
 *
 * Blob ilegível não estoura: vira documento vazio, e quem reclama é o `validate`.
 * A `dificuldade` NÃO trafega no blob (é metadado só do editor) — quando o blob
 * parseia com sucesso ela volta INFERIDA pela contagem de casas dadas (ver
 * `inferDificuldade`, acima); blob ilegível volta no padrão de `emptyDoc` (`'medio'`,
 * coerente com o tabuleiro vazio que ele produz).
 */
export function blobToDoc(raw: unknown, titulo: string): SudokuDoc {
  // Forma não suportada (ou blob ilegível) → documento vazio 9×9; quem reclama é o `validate`.
  const shape = shapeFromGame(raw);
  if (!shape) return emptyDoc(titulo);
  const doc = emptyDoc(titulo, shape);
  const { size } = shape;

  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return doc;
    }
  }

  if (!isRecord(parsed)) return doc;
  const compiler = parsed['crossword-compiler'];
  if (!isRecord(compiler)) return doc;
  const puzzle = compiler['rectangular-puzzle'];
  if (!isRecord(puzzle)) return doc;
  const sudoku = puzzle.sudoku;
  if (!isRecord(sudoku)) return doc;
  const grid = sudoku.grid;
  if (!isRecord(grid)) return doc;
  const cells = Array.isArray(grid.cell) ? grid.cell : [];
  const alphabet =
    isRecord(puzzle.attributes) && typeof puzzle.attributes.alphabet === 'string'
      ? puzzle.attributes.alphabet
      : undefined;
  const values = parseAlphabet(alphabet, shape);

  for (const c of cells) {
    if (!isRecord(c) || !isRecord(c.attributes)) continue;
    const attrs = c.attributes;
    // `x` = linha, `y` = coluna, 1-based (§4.2).
    const row = Number(attrs.x) - 1;
    const col = Number(attrs.y) - 1;
    if (!Number.isInteger(row) || !Number.isInteger(col)) continue;
    if (row < 0 || row >= size || col < 0 || col >= size) continue;

    const index = cellIndex(row, col, size);
    // Posição do símbolo no alfabeto (+1); símbolo desconhecido vira 0.
    const digit = values.indexOf(String(attrs.solution)) + 1;
    doc.solution[index] = digit >= 1 && digit <= size ? digit : 0;
    // `hint` é string: `'true'` na casa dada, `''` na vazia. Qualquer texto não
    // vazio conta como dada — é o mesmo `hint ? true : false` do player.
    doc.givens[index] = typeof attrs.hint === 'string' ? attrs.hint !== '' : Boolean(attrs.hint);
  }

  // A inferência usa os alvos de dadas do 9×9; no 15×15 fica o padrão 'medio'.
  if (size === 9) doc.dificuldade = inferDificuldade(doc.givens);
  return doc;
}
